import { useEventListener } from "@liveblocks/react"
import {
  MetadataBadge,
  MetadataPreview,
  type MetadataType,
  metaDescriptionProperty,
  metaTitleProperty,
} from "@prismicio/editor-fields"
import {
  Box,
  Card,
  CardContent,
  Dialog,
  DialogActionButton,
  DialogActions,
  DialogContent,
  DialogHeader,
  Text,
  TextArea,
} from "@prismicio/editor-ui"
import type { Document } from "@prismicio/types-internal/lib/content"
import { useMemo, useState } from "react"

import { useCollaborationDataFlag } from "../../../app/integrations/AmplitudeExperiments"
import { getReleaseId } from "../../../app/services/release"
import {
  type SeoSuggestionPayloadTracking,
  trackApplyMetadataSuggestion,
  trackDiscardMetadataSuggestion,
} from "../../../app/services/tracking"
import { useCollaborationStorage } from "../../collaboration/CollaborationRoom"
import { getBaseVersion, saveDocument } from "../../documentData"
import { useExistingDocument } from "../../DocumentProvider"
import type { PendingAction } from "./HeaderButtons"

interface MetadataFieldGenerationModalProps {
  initialPageMetadata: MetadataFieldsData
  onOpenChange: (open: boolean) => void
  publish: () => void
  onPendingActionChange: (action: PendingAction) => void
  baseTrackingPayload: SeoSuggestionPayloadTracking
}

interface MetadataFieldData {
  value: string
  isSuggestion: boolean
}

export interface MetadataFieldsData {
  metaTitle: MetadataFieldData
  metaDescription: MetadataFieldData
}

export function MetadataFieldGenerationModal(
  props: MetadataFieldGenerationModalProps,
) {
  const {
    initialPageMetadata,
    onOpenChange,
    publish,
    onPendingActionChange,
    baseTrackingPayload,
  } = props
  const [applyingState, setApplyingState] = useState<
    "discard" | "publish" | undefined
  >(undefined)
  const [isAutoSaving, setIsAutoSaving] = useState(false)
  const canUseCollaborationData = useCollaborationDataFlag() === "on"
  const { content, draft, data } = useExistingDocument()
  const { meta, version } = data
  const storage = useCollaborationStorage({ status: version.status })

  const [metaTitle, setMetaTitle] = useState(
    initialPageMetadata.metaTitle.value,
  )
  const [metaDescription, setMetaDescription] = useState(
    initialPageMetadata.metaDescription.value,
  )

  const trackingPayload = useMemo(
    () => ({
      ...baseTrackingPayload,
      title: getMetadataFieldTrackingPayload(
        initialPageMetadata.metaTitle,
        metaTitle,
      ),
      description: getMetadataFieldTrackingPayload(
        initialPageMetadata.metaDescription,
        metaDescription,
      ),
    }),
    [baseTrackingPayload, initialPageMetadata, metaTitle, metaDescription],
  )

  async function onApplyMetadata() {
    setApplyingState("publish")
    onPendingActionChange("publish")

    const newContent: Document = {
      ...content,
      [metaTitleProperty]: {
        __TYPE__: "FieldContent",
        type: "Text",
        value: metaTitle,
      },
      [metaDescriptionProperty]: {
        __TYPE__: "FieldContent",
        type: "Text",
        value: metaDescription,
      },
    }

    if (canUseCollaborationData) {
      // We just modify the Liveblocks storage, which will trigger the webhook
      // to asynchronously save the document.
      setIsAutoSaving(true)
      void storage?.update({
        document: newContent,
      })
    } else {
      const baseVersion = getBaseVersion(version, meta.versions)
      const releaseId = getReleaseId(baseVersion)

      await saveDocument({
        id: meta.id,
        baseVersionId: baseVersion.version_id,
        releaseId,
        documentTags: draft.documentTags,
        content: newContent,
        status: baseVersion.status,
      })

      void publish()

      trackApplyMetadataSuggestion(trackingPayload)
    }
  }

  function onDiscardMetadata() {
    setApplyingState("discard")
    void publish()

    trackDiscardMetadataSuggestion(baseTrackingPayload)
  }

  return (
    <Dialog
      onOpenChange={onOpenChange}
      open
      size={{
        width: 612,
        height: "auto",
      }}
      onOpenAutoFocus={(evt) => evt.preventDefault()}
      noDescription
    >
      <DialogHeader
        icon="autoFixHigh"
        title="Complete meta information for better SEO"
      />

      <DialogContent>
        {canUseCollaborationData && (
          <LiveblocksAutosave
            publish={publish}
            trackingPayload={trackingPayload}
            setIsAutoSaving={setIsAutoSaving}
            isAutoSaving={isAutoSaving}
          />
        )}

        <Box padding={16} gap={16} flexDirection="column">
          <MetadataPreview title={metaTitle} description={metaDescription} />

          <SuggestionPreview
            name="Meta title"
            type={metaTitleProperty}
            value={metaTitle}
            onChange={setMetaTitle}
            isSuggestion={initialPageMetadata.metaTitle.isSuggestion}
          />

          <SuggestionPreview
            name="Meta description"
            type={metaDescriptionProperty}
            value={metaDescription}
            onChange={setMetaDescription}
            isSuggestion={initialPageMetadata.metaDescription.isSuggestion}
          />
        </Box>
      </DialogContent>

      <DialogActions>
        <DialogActionButton
          onClick={onDiscardMetadata}
          color="grey"
          loading={applyingState === "discard"}
        >
          Discard and publish
        </DialogActionButton>
        <DialogActionButton
          onClick={onApplyMetadata}
          loading={applyingState === "publish"}
        >
          Apply and publish
        </DialogActionButton>
      </DialogActions>
    </Dialog>
  )
}

function LiveblocksAutosave(props: {
  publish: () => void
  trackingPayload: SeoSuggestionPayloadTracking
  setIsAutoSaving: (isAutoSaving: boolean) => void
  isAutoSaving: boolean
}) {
  const { publish, trackingPayload, setIsAutoSaving, isAutoSaving } = props

  // In the case of the collaboration data,
  // since we're using the Liveblocks storage, we are waiting for the autosave
  // event to trigger the publish.
  useEventListener(({ event }) => {
    if (event.type !== "autosave") return

    // We only want to publish the document if the autosave was successful
    if (event.state === "success" && isAutoSaving) {
      void publish()
      trackApplyMetadataSuggestion(trackingPayload)
      setIsAutoSaving(false)
    }
  })

  return null
}

interface SuggestionPreviewProps {
  name: string
  type: MetadataType
  value: string
  onChange: (value: string) => void
  isSuggestion: boolean
}

function SuggestionPreview(props: SuggestionPreviewProps) {
  const { name, type, value, isSuggestion, onChange } = props

  return (
    <Box gap={8} flexDirection="column">
      <Box justifyContent="space-between">
        <Text variant="bold">
          {isSuggestion ? `Suggestion for ${name}` : name}
        </Text>
        <MetadataBadge type={type} value={value} />
      </Box>

      <Card paddingBlock={0} paddingInline={0} variant="elevated">
        <CardContent>
          <TextArea value={value} onChange={onChange} />
        </CardContent>
      </Card>
    </Box>
  )
}

function getMetadataFieldTrackingPayload(
  metaField: MetadataFieldsData[keyof MetadataFieldsData],
  editedValue: string,
) {
  const { isSuggestion, value: originalValue } = metaField

  // We just want to track suggested content
  if (!isSuggestion) return undefined

  return {
    edited: originalValue !== editedValue,
    lengthChange: calculateLengthChangePercentage(originalValue, editedValue),
    characterOverlap: calculateCharacterOverlapPercentage(
      originalValue,
      editedValue,
    ),
  }
}

function calculateLengthChangePercentage(
  original: string,
  edited: string,
): number {
  const originalLength = original.length
  const editedLength = edited.length
  const changePercentage =
    (Math.abs(originalLength - editedLength) / originalLength) * 100

  return Math.round(changePercentage)
}

function calculateCharacterOverlapPercentage(original: string, edited: string) {
  const editedSet = new Set(edited.split(""))
  const intersectionSize = original
    .split("")
    .filter((char) => editedSet.has(char)).length

  const overlapPercentage =
    (intersectionSize / Math.max(original.length, edited.length)) * 100

  return Math.round(overlapPercentage)
}
